/**
 * Global AI Service for Client-Side Operations
 * Handles model fetching and chat streaming via Supabase Edge Function proxy.
 */

import { AIProxyHttpError, aiProxyClient } from "@/services/aiProxyClient";
import { getChatStreamTimeoutMs, type ChatStreamTimingSession } from "@/lib/chatStreamTiming";
import { logOutboundChatPayload } from "@/lib/chatHistoryLimits";
import {
    affordableOutputRetryCap,
    extractOpenRouterErrorMessage,
    isWebSearchRelatedError,
    parseAffordableMaxTokens,
    resolveChatMaxTokens,
} from "@/lib/openRouterErrors";
import { modelSupportsWebSearch } from "@/lib/openRouterModelAliases";
import { parseChatError } from "@/lib/chatErrorCatalog";

const LOOP_WINDOW_CHARS = 500;
const LOOP_SUBSTR_LEN = 80;
const LOOP_REPEAT_THRESHOLD = 8;

function detectRepetitiveLoop(rollingContent: string, newContent: string): boolean {
  const window = (rollingContent + newContent).slice(-LOOP_WINDOW_CHARS);
  if (window.length < LOOP_SUBSTR_LEN * 2) return false;
  const tail = window.slice(-LOOP_SUBSTR_LEN);
  let count = 0;
  let idx = 0;
  while ((idx = window.indexOf(tail, idx)) !== -1) {
    count++;
    if (count >= LOOP_REPEAT_THRESHOLD) return true;
    idx += 1;
  }
  return false;
}

/** Rewrite raw provider failures. Leave messages that are already user-facing unchanged. */
function presentStreamError(message: string): string {
  if (
    parseAffordableMaxTokens(message) != null ||
    /https?:\/\//i.test(message) ||
    /requires more credits|insufficient credits|payment required|prompt tokens limit exceeded|openrouter_credits/i.test(message)
  ) {
    return parseChatError(message).message;
  }
  return message;
}

function isAbortError(error: unknown): boolean {
  return (
    (error instanceof DOMException && error.name === "AbortError") ||
    (error instanceof Error && error.name === "AbortError")
  );
}

export interface OpenRouterModel {
    id: string;
    name: string;
    description?: string;
    pricing?: {
        prompt?: string;
        completion?: string;
    };
    context_length?: number;
    architecture?: {
        modality?: string;
        tokenizer?: string;
        instruct_type?: string;
        output_modalities?: string[];
        input_modalities?: string[];
    };
    top_provider?: {
        name?: string;
        max_completion_tokens?: number;
    };
    supported_parameters?: string[];
}

export interface ChatMessage {
    role: "user" | "assistant" | "system";
    content: string | Array<{
        type: string;
        text?: string;
        image_url?: { url: string; detail?: string };
        input_audio?: { data: string };
        videoUrl?: { url: string };
        file?: { filename?: string; file_data?: string };
    }>;
}

export interface StreamingChunk {
    type: "delta" | "citations" | "images" | "personalization" | "done" | "error" | "reasoning" | "notice";
    content?: string;
    reasoningContent?: string;
    citations?: Array<{
        type: "url_citation";
        url_citation: {
            url: string;
            title?: string;
            content?: string;
            start_index?: number;
            end_index?: number;
        };
    }>;
    images?: Array<{ url: string; detail?: string }>;
    layers?: any;
    error?: string;
    usage?: {
        prompt_tokens?: number;
        completion_tokens?: number;
        total_tokens?: number;
    };
}

export interface ChatOptions {
    model: string;
    messages: ChatMessage[];
    modelInfo?: OpenRouterModel;
    stream?: boolean;
    temperature?: number;
    maxTokens?: number;
    /** When true, attach OpenRouter web search server tool (no :online suffix). */
    webSearchEnabled?: boolean;
    /** Dev / VITE_CHAT_TIMING — phase marks for chatStreamTiming */
    timing?: ChatStreamTimingSession | null;
    /** Product surface for ai_usage_logs x-app (chat, mcp, agents, ...). */
    app?: string;
}

/**
 * True if the model supports text input and text output (excludes audio-only / speech models).
 * Prevents "This model requires that either input content or output modality contain audio" 400.
 */
function isTextChatModel(model: OpenRouterModel): boolean {
    const out = model.architecture?.output_modalities;
    const inp = model.architecture?.input_modalities;
    if (out?.length && !out.includes("text")) return false;
    if (inp?.length && !inp.includes("text")) return false;
    const label = `${model.name ?? ""} ${model.id ?? ""}`.toLowerCase();
    if (label.includes("whisper") || label.includes("speech") || (label.includes("audio") && !label.includes("text"))) return false;
    return true;
}

function extractChunkText(parsedChunk: any): string {
    const choice = parsedChunk?.choices?.[0];
    const delta = choice?.delta;
    const message = choice?.message;

    if (typeof delta?.content === "string" && delta.content.length > 0) return delta.content;
    if (Array.isArray(delta?.content)) {
        const txt = delta.content
            .map((p: any) => (p?.type === "text" && typeof p.text === "string" ? p.text : ""))
            .join("");
        if (txt) return txt;
    }
    if (typeof message?.content === "string" && message.content.length > 0) return message.content;
    if (Array.isArray(message?.content)) {
        const txt = message.content
            .map((p: any) => (p?.type === "text" && typeof p.text === "string" ? p.text : ""))
            .join("");
        if (txt) return txt;
    }
    if (typeof choice?.text === "string" && choice.text.length > 0) return choice.text;
    return "";
}

class AIService {
    constructor() {}

    /**
     * Normalize message content to OpenRouter/OpenAI format (snake_case keys).
     * Fixes videoUrl -> video_url, strips unsupported part types, ensures valid structure.
     */
    private normalizeMessagesForOpenRouter(messages: ChatMessage[]): ChatMessage[] {
        return messages.map((msg) => {
            if (typeof msg.content === "string") {
                return msg;
            }
            if (!Array.isArray(msg.content)) {
                return { ...msg, content: String(msg.content ?? "") };
            }
            const parts = msg.content
                .map((part: any) => {
                    if (!part || typeof part !== "object") return null;
                    if (part.type === "text") {
                        const text = String(part.text ?? "").trim();
                        if (text === "") return null;
                        return { type: "text", text: part.text ?? "" };
                    }
                    if (part.type === "image_url") {
                        const url = part.image_url?.url ?? part.imageUrl?.url;
                        if (!url) return null;
                        return { type: "image_url", image_url: { url, detail: part.image_url?.detail ?? part.imageUrl?.detail } };
                    }
                    if (part.type === "video_url") {
                        const url = part.video_url?.url ?? part.videoUrl?.url;
                        if (!url) return null;
                        return { type: "video_url", video_url: { url } };
                    }
                    if (part.type === "input_audio") {
                        const data = part.input_audio?.data ?? part.inputAudio?.data;
                        if (!data) return null;
                        return { type: "input_audio", input_audio: { data, format: part.input_audio?.format ?? part.inputAudio?.format ?? "wav" } };
                    }
                    // OpenRouter supports PDF/file via type "file" with file_data (data URL or URL)
                    if (part.type === "file") {
                        const file = part.file;
                        const fileData = file?.file_data ?? file?.fileData;
                        if (!fileData) return null;
                        return { type: "file", file: { filename: file?.filename, file_data: fileData } };
                    }
                    return null;
                })
                .filter(Boolean);
            if (parts.length === 0) {
                return { ...msg, content: " " };
            }
            if (parts.length === 1 && (parts[0] as any).type === "text") {
                const text = (parts[0] as any).text;
                const str = typeof text === "string" ? text.trim() : "";
                return { ...msg, content: str || " " };
            }
            return { ...msg, content: parts };
        });
    }

    /**
     * Fetch available models from OpenRouter
     */
    async fetchModels(): Promise<OpenRouterModel[]> {
        try {
            console.log(`[AI Service] Fetching models from OpenRouter API...`);
            const all = await aiProxyClient.getModels();
            console.log(`[AI Service] Models API Response - Total models received:`, all.length);
            console.log(`[AI Service] Full models response:`, all);

            // Exclude audio-only models so text chat never selects them (avoids "requires audio" 400)
            const filtered = all.filter(
                (m: OpenRouterModel) => typeof m.id === "string" && m.id.trim().length > 0 && isTextChatModel(m)
            );
            console.log(`[AI Service] Filtered models (text-chat only):`, filtered.length, filtered);
            return filtered;
        } catch (error) {
            console.error("Error fetching OpenRouter models:", error);
            throw error;
        }
    }

    /**
     * Stream chat completion from OpenRouter
     * Returns an async generator that yields streaming chunks
     */
    async* streamChat(options: ChatOptions): AsyncGenerator<StreamingChunk, void, unknown> {
        const {
            model: rawModel,
            messages,
            modelInfo,
            stream = true,
            temperature = 0.7,
            maxTokens,
            webSearchEnabled = false,
            timing,
            app,
        } = options;

        const model = rawModel.replace(/:online$/i, "").trim();

        if (!model) {
            yield {
                type: "error",
                error: "No model selected. Please select a model before sending a message.",
            };
            return;
        }

        const lastMessage = messages[messages.length - 1];
        const hasImageUrl = Array.isArray(lastMessage?.content)
            ? lastMessage.content.some((item: any) => item.type === "image_url")
            : false;

        const modalities = modelInfo?.architecture?.output_modalities || ["text"];
        const isImageGeneratingModel = modalities.includes("image");
        const shouldStream = stream && !hasImageUrl && !isImageGeneratingModel;
        const normalizedMessages = this.normalizeMessagesForOpenRouter(messages);

        const useWebSearch =
            webSearchEnabled &&
            modelSupportsWebSearch(model, modelInfo?.supported_parameters);

        let outputCap = resolveChatMaxTokens(
            maxTokens,
            modelInfo?.top_provider?.max_completion_tokens,
        );
        let attemptedAffordableRetry = false;

        const buildBody = (includeWebSearch: boolean): Record<string, unknown> => {
            const body: Record<string, unknown> = {
                model,
                messages: normalizedMessages,
                stream: shouldStream,
                temperature: Number.isFinite(temperature) ? temperature : 0.7,
                max_tokens: outputCap,
            };
            if (isImageGeneratingModel && modalities.includes("image")) {
                body.modalities = ["text", "image"];
            }
            const hasFilePart = normalizedMessages.some((m: ChatMessage) =>
                Array.isArray(m.content) && m.content.some((p: any) => p?.type === "file")
            );
            if (hasFilePart) {
                const supportsFileNatively = modelInfo?.architecture?.input_modalities?.includes("file");
                body.plugins = [{
                    id: "file-parser",
                    pdf: { engine: supportsFileNatively ? "native" : "pdf-text" },
                }];
            }
            if (includeWebSearch) {
                body.tools = [{ type: "openrouter:web_search" }];
            }
            return body;
        };

        let attemptedWithoutWeb = !useWebSearch;
        let includeWebSearch = useWebSearch;

        while (true) {
            const body = buildBody(includeWebSearch);
            let failedWithWebError = false;
            let retrySmallerOutput = false;

            try {
                for await (const chunk of this.runChatStream({
                    body,
                    model,
                    shouldStream,
                    normalizedMessages,
                    timing,
                    app,
                })) {
                    if (chunk.type === "error") {
                        const errMsg = typeof chunk.error === "string" ? chunk.error : "";
                        const nextCap = affordableOutputRetryCap(
                            errMsg,
                            outputCap,
                            attemptedAffordableRetry,
                        );
                        if (nextCap != null) {
                            attemptedAffordableRetry = true;
                            outputCap = nextCap;
                            retrySmallerOutput = true;
                            console.info("[streamChat] Retrying with a smaller output cap", {
                                maxTokens: nextCap,
                            });
                            break;
                        }
                        if (includeWebSearch && !attemptedWithoutWeb && isWebSearchRelatedError(errMsg)) {
                            failedWithWebError = true;
                            break;
                        }
                        yield { ...chunk, error: presentStreamError(errMsg) };
                        return;
                    }
                    yield chunk;
                    if (chunk.type === "done") return;
                }
            } catch (error) {
                const msg =
                    error instanceof AIProxyHttpError
                        ? error.message
                        : error instanceof Error
                          ? error.message
                          : "";
                const nextCap = affordableOutputRetryCap(msg, outputCap, attemptedAffordableRetry);
                if (nextCap != null) {
                    attemptedAffordableRetry = true;
                    outputCap = nextCap;
                    retrySmallerOutput = true;
                    console.info("[streamChat] Retrying with a smaller output cap", {
                        maxTokens: nextCap,
                    });
                } else if (includeWebSearch && !attemptedWithoutWeb && isWebSearchRelatedError(msg)) {
                    failedWithWebError = true;
                } else {
                    throw error;
                }
            }

            if (retrySmallerOutput) continue;
            if (!failedWithWebError) return;

            includeWebSearch = false;
            attemptedWithoutWeb = true;
            yield {
                type: "notice",
                content: "Web search unavailable for this model; answering without live web data.",
            };
        }
    }

    private async* runChatStream(args: {
        body: Record<string, unknown>;
        model: string;
        shouldStream: boolean;
        normalizedMessages: ChatMessage[];
        timing?: ChatStreamTimingSession | null;
        app?: string;
    }): AsyncGenerator<StreamingChunk, void, unknown> {
        const { body, model, shouldStream, normalizedMessages, timing, app } = args;

        if (import.meta.env.DEV) {
            logOutboundChatPayload("aiService", normalizedMessages, {
                model,
                shouldStream,
                bodyPayloadChars: (() => {
                    try {
                        return JSON.stringify(body).length;
                    } catch {
                        return undefined;
                    }
                })(),
            });
        }

        let sawFirstToken = false;
        let sawFirstReasoning = false;

        const streamTimeoutMs = getChatStreamTimeoutMs();
        const streamAbort = new AbortController();
        let streamTimeoutId: ReturnType<typeof setTimeout> | null = null;
        let timedOut = false;
        if (shouldStream && streamTimeoutMs > 0) {
            streamTimeoutId = setTimeout(() => {
                timedOut = true;
                timing?.mark("stream_timeout_fired");
                streamAbort.abort(new DOMException("Chat stream exceeded maximum duration", "TimeoutError"));
            }, streamTimeoutMs);
        }

        try {
            timing?.mark("fetch_start");
            const response = await aiProxyClient.chatCompletions(body as any, {
                stream: shouldStream,
                signal: streamAbort.signal,
                app,
            });

            if (!response.ok) {
                const errorText = await response.text();
                let errorMessage = `API Error: ${response.status} ${response.statusText}`;
                try {
                    const errorJson = JSON.parse(errorText);
                    errorMessage =
                        extractOpenRouterErrorMessage(errorJson) ??
                        errorMessage;
                    if (import.meta.env.DEV) {
                        console.error("[OpenRouter] Full error response:", JSON.stringify(errorJson, null, 2));
                    }
                } catch {
                    errorMessage =
                        (extractOpenRouterErrorMessage(errorText) ?? errorText) || errorMessage;
                    if (import.meta.env.DEV) {
                        console.error("[OpenRouter] Raw error response:", errorText);
                    }
                }
                yield { type: "error", error: errorMessage };
                return;
            }

            // Handle non-streaming response (when images are present)
            if (!shouldStream) {
                try {
                    const jsonResponse = await response.json();
                    const message = jsonResponse.choices?.[0]?.message;
                    const content = extractChunkText(jsonResponse);

                    // Capture usage information if present
                    const usage = jsonResponse.usage ? {
                        prompt_tokens: jsonResponse.usage.prompt_tokens,
                        completion_tokens: jsonResponse.usage.completion_tokens,
                        total_tokens: jsonResponse.usage.total_tokens,
                    } : undefined;

                    if (content) {
                        if (!sawFirstToken) {
                            sawFirstToken = true;
                            timing?.mark("first_token");
                        }
                        yield { type: "delta", content };
                    }

                    // Yield citations if present
                    if (message?.annotations && Array.isArray(message.annotations) && message.annotations.length > 0) {
                        yield {
                            type: "citations",
                            citations: message.annotations.map((a: any) => ({
                                type: "url_citation" as const,
                                url_citation: {
                                    url: a.url_citation?.url || "",
                                    title: a.url_citation?.title,
                                    content: a.url_citation?.content,
                                    start_index: a.url_citation?.start_index,
                                    end_index: a.url_citation?.end_index,
                                },
                            })),
                        };
                    }

                    // Yield images if present
                    if (message?.images && Array.isArray(message.images) && message.images.length > 0) {
                        yield {
                            type: "images",
                            images: message.images.map((img: any) => ({
                                url: img.image_url?.url || img.url || "",
                                detail: img.image_url?.detail || "auto",
                            })),
                        };
                    }

                    yield { 
                        type: "done",
                        usage: usage
                    };
                    return;
                } catch (error) {
                    console.error("Failed to parse non-streaming response:", error);
                    yield {
                        type: "error",
                        error: `Failed to parse API response: ${error instanceof Error ? error.message : "Unknown error"}`,
                    };
                    return;
                }
            }

            // Handle streaming response
            if (!response.body) {
                yield { type: "error", error: "Response body is null" };
                return;
            }

            const decoder = new TextDecoder("utf-8");
            const reader = response.body.getReader();
            let buffer = "";
            let lastUsage: { prompt_tokens?: number; completion_tokens?: number; total_tokens?: number } | undefined;
            let sawFirstByte = false;
            let sawImages = false;
            let rollingStreamContent = "";
            let loopDetected = false;

            try {
                while (true) {
                    if (streamAbort.signal.aborted) break;

                    let done: boolean;
                    let value: Uint8Array | undefined;
                    try {
                        ({ done, value } = await reader.read());
                    } catch (readErr) {
                        if (streamAbort.signal.aborted || timedOut) break;
                        throw readErr;
                    }
                    if (done) break;

                    if (!sawFirstByte && value && value.byteLength > 0) {
                        sawFirstByte = true;
                        timing?.mark("first_byte");
                    }

                    buffer += decoder.decode(value, { stream: true });
                    const lines = buffer.split("\n");
                    buffer = lines.pop() || "";

                    for (const line of lines) {
                        const trimmed = line.trim();
                        if (!trimmed || trimmed.startsWith(":")) continue;

                        if (trimmed.startsWith("data:")) {
                            const data = trimmed.slice(5).trimStart();

                            if (data === "[DONE]") {
                                yield { 
                                    type: "done",
                                    usage: lastUsage
                                };
                                return;
                            }

                            try {
                                const parsedChunk = JSON.parse(data);
                                const content = extractChunkText(parsedChunk);
                                const delta = parsedChunk.choices?.[0]?.delta;
                                const message = parsedChunk.choices?.[0]?.message;

                                // Capture usage information if present (OpenRouter includes it in chunks)
                                if (parsedChunk.usage) {
                                    lastUsage = {
                                        prompt_tokens: parsedChunk.usage.prompt_tokens,
                                        completion_tokens: parsedChunk.usage.completion_tokens,
                                        total_tokens: parsedChunk.usage.total_tokens,
                                    };
                                }

                                // Yield content chunks
                                if (content) {
                                    if (!sawFirstToken) {
                                        sawFirstToken = true;
                                        timing?.mark("first_token");
                                    }
                                    if (detectRepetitiveLoop(rollingStreamContent, content)) {
                                        loopDetected = true;
                                        streamAbort.abort();
                                        break;
                                    }
                                    rollingStreamContent = (rollingStreamContent + content).slice(-LOOP_WINDOW_CHARS);
                                    yield { type: "delta", content };
                                }

                                // Yield reasoning/thinking from delta (OpenRouter: reasoning_details or reasoning)
                                const reasoningDetails = delta?.reasoning_details;
                                if (Array.isArray(reasoningDetails) && reasoningDetails.length > 0) {
                                    for (const detail of reasoningDetails) {
                                        if (detail?.type === "reasoning.text" && typeof detail.text === "string" && detail.text) {
                                            if (!sawFirstReasoning) {
                                                sawFirstReasoning = true;
                                                timing?.mark("first_reasoning");
                                            }
                                            yield { type: "reasoning", reasoningContent: detail.text };
                                        }
                                        if (detail?.type === "reasoning.summary" && typeof detail.summary === "string" && detail.summary) {
                                            if (!sawFirstReasoning) {
                                                sawFirstReasoning = true;
                                                timing?.mark("first_reasoning");
                                            }
                                            yield { type: "reasoning", reasoningContent: detail.summary };
                                        }
                                    }
                                }
                                const reasoningStr = delta?.reasoning ?? (delta as any)?.reasoning_content;
                                if (typeof reasoningStr === "string" && reasoningStr) {
                                    if (!sawFirstReasoning) {
                                        sawFirstReasoning = true;
                                        timing?.mark("first_reasoning");
                                    }
                                    yield { type: "reasoning", reasoningContent: reasoningStr };
                                }

                                // Yield annotations/citations
                                if (delta && "annotations" in delta && delta.annotations) {
                                    const annotations = delta.annotations as any[];
                                    if (annotations.length > 0) {
                                        yield {
                                            type: "citations",
                                            citations: annotations.map((a: any) => ({
                                                type: "url_citation" as const,
                                                url_citation: {
                                                    url: a.url_citation?.url || "",
                                                    title: a.url_citation?.title,
                                                    content: a.url_citation?.content,
                                                    start_index: a.url_citation?.start_index,
                                                    end_index: a.url_citation?.end_index,
                                                },
                                            })),
                                        };
                                    }
                                }

                                // Yield images from message
                                if (message?.images && Array.isArray(message.images) && message.images.length > 0) {
                                    sawImages = true;
                                    yield {
                                        type: "images",
                                        images: message.images.map((img: any) => ({
                                            url: img.image_url?.url || img.url || "",
                                            detail: img.image_url?.detail || "auto",
                                        })),
                                    };
                                }

                                // Yield images from delta
                                if (delta?.images && Array.isArray(delta.images) && delta.images.length > 0) {
                                    sawImages = true;
                                    yield {
                                        type: "images",
                                        images: delta.images.map((img: any) => ({
                                            url: img.image_url?.url || img.url || "",
                                            detail: img.image_url?.detail || "auto",
                                        })),
                                    };
                                }
                            } catch (error) {
                                // Skip parse errors for partial/incomplete JSON
                                if (error instanceof SyntaxError) {
                                    continue;
                                }
                                console.error("Failed to parse stream chunk:", data, error);
                            }
                        }
                    }
                    if (loopDetected || streamAbort.signal.aborted) break;
                }
            } finally {
                if (streamAbort.signal.aborted) {
                    await reader.cancel().catch(() => {});
                }
                reader.releaseLock();
            }

            if (loopDetected) {
                yield {
                    type: "error",
                    error: parseChatError("Model output appears stuck in a loop. Try Retry or a different model.").message,
                };
                return;
            }

            if (streamAbort.signal.aborted && timedOut) {
                yield {
                    type: "error",
                    error: parseChatError(new DOMException("Chat stream exceeded maximum duration", "TimeoutError"), {
                        timedOut: true,
                    }).message,
                };
                return;
            }

            if (streamAbort.signal.aborted) {
                yield {
                    type: "error",
                    error: parseChatError(new DOMException("Aborted", "AbortError")).message,
                };
                return;
            }

            if (!sawFirstToken && !sawFirstReasoning && !sawImages) {
                yield {
                    type: "error",
                    error: parseChatError(
                        "The model returned an empty response. Verify the OpenRouter API key in Supabase and try a different model."
                    ).message,
                };
                return;
            }

            yield { 
                type: "done",
                usage: lastUsage
            };
        } catch (error) {
            if (error instanceof AIProxyHttpError) {
                console.error("[streamChat:init] Proxy HTTP failure", {
                    status: error.status,
                    statusText: error.statusText,
                    url: error.url,
                    headers: error.headers,
                    bodyText: error.bodyText,
                    bodyJson: error.bodyJson,
                    model,
                    shouldStream,
                    messageCount: normalizedMessages.length,
                    hasFilePart: normalizedMessages.some((m: ChatMessage) =>
                        Array.isArray(m.content) && m.content.some((p: any) => p?.type === "file")
                    ),
                });
            } else if (isAbortError(error) && (timedOut || streamAbort.signal.aborted)) {
                if (import.meta.env.DEV) {
                    console.info("[streamChat] Stream stopped", { timedOut, streamTimeoutMs });
                }
            } else {
                console.error("Streaming error:", error);
            }
            const rawProviderMessage =
                error instanceof AIProxyHttpError
                    ? (extractOpenRouterErrorMessage(error.bodyJson) ??
                      extractOpenRouterErrorMessage(error.bodyText) ??
                      error.message)
                    : null;
            if (rawProviderMessage) {
                yield { type: "error", error: rawProviderMessage };
            } else {
                const parsed = parseChatError(error, {
                    timedOut,
                    httpStatus: error instanceof AIProxyHttpError ? error.status : undefined,
                });
                yield { type: "error", error: parsed.message };
            }
        } finally {
            if (streamTimeoutId != null) {
                clearTimeout(streamTimeoutId);
                streamTimeoutId = null;
            }
        }
    }

    /**
     * Check if API key is configured
     */
    isConfigured(): boolean {
        return true;
    }

    /**
     * Simple non-streaming completion for internal use (personalization, etc.)
     * Uses a fast, cheap model for quick responses
     */
    async complete(
        prompt: string,
        options?: { model?: string; maxTokens?: number; temperature?: number; suppressErrors?: boolean; app?: string }
    ): Promise<string> {
        const model = options?.model || "openai/gpt-4o-mini";
        const maxTokens = options?.maxTokens || 500;
        const temperature = options?.temperature ?? 0.7;
        const suppressErrors = options?.suppressErrors ?? false;

        try {
            const response = await aiProxyClient.chatCompletions({
                model,
                messages: [{ role: "user", content: prompt }],
                stream: false,
                temperature,
                max_tokens: maxTokens,
            }, { app: options?.app });
            const data = await response.json();
            return data.choices?.[0]?.message?.content || "";
        } catch (error) {
            if (!suppressErrors) {
                console.error("AI completion error:", error);
            }
            throw error;
        }
    }
}

// Export singleton instance
export const aiService = new AIService();

// Export types for use in components
export type { ChatOptions, StreamingChunk, ChatMessage, OpenRouterModel };





